import { Controllers, ProblemResult, IsBadRequestResult, IsServerErrorResult, IsNotFoundResult, IsForbiddenResult } from "@infoland/iprova-api";
import storeHelper from "@/store/store-helper";
import { RootState } from "@/store";
import { ActionTree, GetterTree, Dispatch, Module, MutationTree } from "vuex";
import { TaskFilterOption } from "@/enums";
import { loadMyTasksAsync, Task, ApiPostTask, loadTasksForTeamAsync } from "@/tasks";
import moment from "moment";

// state type
export interface TasksState {
    tasks: Task[];
    filteredTasks: Task[];
    selectedFilter: TaskFilterOption;
}

export function initialize(): TasksState {
    return {
        tasks: [],
        filteredTasks: [],
        selectedFilter: TaskFilterOption.My
    };
}

export interface ActionResult<S, E> {
    success: boolean;
    successData?: S;
    errorData?: E;
}

// initial state
const state: TasksState = initialize();

// getters for this store
const getters: GetterTree<TasksState, RootState> = {
    tasks(state: TasksState): Task[] {
        return state.tasks;
    },
    filteredTasks(state: TasksState): Task[] {
        return state.filteredTasks;
    },
    selectedFilter(state: TasksState): TaskFilterOption {
        return state.selectedFilter;
    }
};

const mutations: MutationTree<TasksState> = {
    _tasks(state: TasksState, tasks: Task[]): void {
        state.tasks = tasks;
    },
    _updateReadState(state: TasksState, { task_id, isRead }: { task_id: string; isRead: boolean }): void {
        const task = state.tasks.find(t => t.id == task_id);
        if (task) task.read = isRead;
    },
    _filteredTasks(state: TasksState, filteredTasks: Task[]): void {
        state.filteredTasks = filteredTasks;
    },
    _selectedFilter(state: TasksState, filter: TaskFilterOption): void {
        state.selectedFilter = filter;
    },
    _updatePostponeDate(state: TasksState, { task_id, postponedUntilDate }): void {
        const task = state.tasks.find(x => x.id == task_id);
        const filteredTask = state.filteredTasks.find(x => x.id == task_id);
        //team tasks are not present in the store
        if (!task) return;
        if (!filteredTask) return;

        task.postponed_until_date = !postponedUntilDate ? null : (postponedUntilDate as Date);
        filteredTask.postponed_until_date = !postponedUntilDate ? null : (postponedUntilDate as Date);
    },

    _updateIsImportant(state: TasksState, { taskId, isImportant }): void {
        const task = state.tasks.find(x => x.id == taskId);
        const filteredTask = state.filteredTasks.find(x => x.id == taskId);
        //team tasks are not present in the store
        if (!task) return;
        if (!filteredTask) return;

        task.is_important = isImportant;
        filteredTask.is_important = isImportant;
    }
};

function handleTasksMutationResult(result: void | ProblemResult<string>, dispatch: Dispatch): ActionResult<void, string> {
    if (IsServerErrorResult(result) || IsBadRequestResult(result) || IsNotFoundResult(result) || IsForbiddenResult(result)) {
        return { success: false };
    } else {
        return { success: true };
    }
}

const actions: ActionTree<TasksState, RootState> = {
    async loadTasksAsync({ commit }): Promise<void> {
        const storeTasks = await loadMyTasksAsync();
        commit("_tasks", storeTasks);
        commit("_filteredTasks", storeTasks);
    },
    // Update task read property
    async updateReadTasksAsync({ commit, state }, { task_id, isRead }): Promise<void> {
        await Controllers.GenericController.patchAsync<{ read: boolean }, void>(`tasks/${task_id}`, {
            read: isRead
        });
        commit("_updateReadState", { task_id, isRead });
    },
    // Update task is_important property
    async updateIsImportantTaskAsync({ dispatch, commit, state }, { task_id, is_important }): Promise<void> {
        const result = await Controllers.GenericController.patchAsync<{ is_important: boolean }, void | ProblemResult<string>>(`tasks/${task_id}`, {
            is_important: is_important
        });
        if (handleTasksMutationResult(result, dispatch).success) {
            commit("_updateIsImportant", { taskId: task_id, isImportant: is_important });
        }
    },
    // Update the filtered tasks list
    updateFilteredTasks({ commit, state }, { tasks }): void {
        commit("_filteredTasks", tasks);
    },
    // Update the selected filter
    updateSelectedFilter({ commit, state }, { filter }): void {
        commit("_selectedFilter", filter);
    },
    // Update postponed_until_date property
    async updatePostponedUntilDateTaskAsync({ state, commit, dispatch }, { task_id, postponedUntilDate }): Promise<void> {
        const result = await Controllers.GenericController.patchAsync<{ postponed_until_date: string }, void | ProblemResult<string>>(
            `tasks/${task_id}`,
            {
                postponed_until_date: postponedUntilDate ? postponedUntilDate.toAPIDateString() : "" // empty string will clear the current postponedUntilDate
            }
        );

        if (handleTasksMutationResult(result, dispatch).success) {
            commit("_updatePostponeDate", { task_id, postponedUntilDate });
        }
    }
};

// export the namespace
export const namespace = "tasks";

// export the store 'module'
export default <Module<TasksState, RootState>>{
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
